import React, { useEffect, useState } from 'react'
import {
	DatePicker,
	Drawer,
	Input,
	List,
	Spin
} from 'antd'
import moment, { Moment } from 'moment'
import { getReq } from '../../utils/request'
import { LABEL_LINKED_ENTITY_TYPE } from './types'
import { assignManagerTaskLabelEntity } from './managerTaskApi'

interface Props {
	entityType: LABEL_LINKED_ENTITY_TYPE
	taskId?: number
	labelId: number
	onAssigned?: (displayName: string) => void
	onClose: () => void
	onSelect?: (fkField: string, entityId: number, displayName: string) => void
}

interface SearchResult {
	id: number
	displayName: string
}

const ENTITY_TYPE_LABELS: Record<LABEL_LINKED_ENTITY_TYPE, string> = {
	[LABEL_LINKED_ENTITY_TYPE.JOB]: 'Zákazky',
	[LABEL_LINKED_ENTITY_TYPE.FINISH]: 'Ukončenie',
	[LABEL_LINKED_ENTITY_TYPE.RATING]: 'Hodnotenie',
	[LABEL_LINKED_ENTITY_TYPE.PAYSLIP]: 'Výplatné pásky',
	[LABEL_LINKED_ENTITY_TYPE.USER]: 'Používatelia',
	[LABEL_LINKED_ENTITY_TYPE.PRICE_OFFER]: 'Cenová ponuka',
	[LABEL_LINKED_ENTITY_TYPE.COMPLAINT]: 'Reklamácie'
}

const ENTITY_TYPE_TO_FK: Record<LABEL_LINKED_ENTITY_TYPE, string> = {
	[LABEL_LINKED_ENTITY_TYPE.JOB]: 'jobId',
	[LABEL_LINKED_ENTITY_TYPE.FINISH]: 'finishId',
	[LABEL_LINKED_ENTITY_TYPE.RATING]: 'ratingId',
	[LABEL_LINKED_ENTITY_TYPE.PAYSLIP]: 'payslipId',
	[LABEL_LINKED_ENTITY_TYPE.USER]: 'userId',
	[LABEL_LINKED_ENTITY_TYPE.PRICE_OFFER]: 'priceOfferId',
	[LABEL_LINKED_ENTITY_TYPE.COMPLAINT]: 'complaintId'
}

const ENTITY_ENDPOINTS: Record<LABEL_LINKED_ENTITY_TYPE, string> = {
	[LABEL_LINKED_ENTITY_TYPE.JOB]: '/api/admin/jobs',
	[LABEL_LINKED_ENTITY_TYPE.FINISH]: '/api/admin/finishes',
	[LABEL_LINKED_ENTITY_TYPE.RATING]: '/api/admin/ratings',
	[LABEL_LINKED_ENTITY_TYPE.PAYSLIP]: '/api/admin/payslips',
	[LABEL_LINKED_ENTITY_TYPE.USER]: '/api/admin/users',
	[LABEL_LINKED_ENTITY_TYPE.PRICE_OFFER]: '/api/admin/price-offers',
	[LABEL_LINKED_ENTITY_TYPE.COMPLAINT]: '/api/admin/complaints'
}

// Key inside the API response envelope that holds the array of items
const ENTITY_RESPONSE_KEY: Record<LABEL_LINKED_ENTITY_TYPE, string> = {
	[LABEL_LINKED_ENTITY_TYPE.JOB]: 'jobs',
	[LABEL_LINKED_ENTITY_TYPE.FINISH]: 'finishes',
	[LABEL_LINKED_ENTITY_TYPE.RATING]: 'ratings',
	[LABEL_LINKED_ENTITY_TYPE.PAYSLIP]: 'payslips',
	[LABEL_LINKED_ENTITY_TYPE.USER]: 'users',
	[LABEL_LINKED_ENTITY_TYPE.PRICE_OFFER]: 'priceOffers',
	[LABEL_LINKED_ENTITY_TYPE.COMPLAINT]: 'complaints'
}

const formatResult = (entityType: LABEL_LINKED_ENTITY_TYPE, item: any): SearchResult => {
	const { id } = item
	switch (entityType) {
		case LABEL_LINKED_ENTITY_TYPE.JOB: {
			const name = item.name || item.address || `#${id}`
			return { id, displayName: `#${id} — ${name}` }
		}
		case LABEL_LINKED_ENTITY_TYPE.FINISH: {
			const date = item.date || item.createdAt?.slice(0, 10) || ''
			const jobName = item.jobName || ''
			return { id, displayName: `#${id} — ${date}${jobName ? ` — ${jobName}` : ''}` }
		}
		case LABEL_LINKED_ENTITY_TYPE.RATING: {
			const date = item.date || item.createdAt?.slice(0, 10) || ''
			const score = item.score !== undefined ? ` — ${item.score}★` : ''
			return { id, displayName: `#${id} — ${date}${score}` }
		}
		case LABEL_LINKED_ENTITY_TYPE.PAYSLIP: {
			const month = item.month || item.createdAt?.slice(0, 7) || ''
			const userName = item.userName || [item.firstName, item.lastName].filter(Boolean).join(' ')
			return { id, displayName: `#${id} — ${month}${userName ? ` — ${userName}` : ''}` }
		}
		case LABEL_LINKED_ENTITY_TYPE.USER: {
			const name = [item.firstName, item.lastName].filter(Boolean).join(' ') || item.email || `#${id}`
			return { id, displayName: `#${id} — ${name}` }
		}
		case LABEL_LINKED_ENTITY_TYPE.PRICE_OFFER: {
			const title = item.title || item.name || `#${id}`
			return { id, displayName: `#${id} — ${title}` }
		}
		case LABEL_LINKED_ENTITY_TYPE.COMPLAINT: {
			const date = item.date || item.createdAt?.slice(0, 10) || ''
			const desc = item.description ? ` — ${String(item.description).slice(0, 40)}` : ''
			return { id, displayName: `#${id} — ${date}${desc}` }
		}
		default:
			return { id, displayName: `#${id}` }
	}
}

const EntitySearchDrawer = ({
	entityType,
	taskId,
	labelId,
	onAssigned,
	onClose,
	onSelect
}: Props) => {
	const [search, setSearch] = useState('')
	const [payslipMonth, setPayslipMonth] = useState<Moment>(moment())
	const [results, setResults] = useState<SearchResult[]>([])
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		let cancelled = false
		const fetchResults = async () => {
			setLoading(true)
			try {
				const endpoint = ENTITY_ENDPOINTS[entityType]
				const queryParams = {
					search,
					limit: 20,
					...(entityType === LABEL_LINKED_ENTITY_TYPE.PAYSLIP ? { month: payslipMonth.format('YYYY-MM') } : {})
				}
				const { data } = await getReq(endpoint, queryParams)
				if (!cancelled) {
					const key = ENTITY_RESPONSE_KEY[entityType]
					const items: any[] = Array.isArray(data) ? data : (data?.[key] || data?.data || [])
					setResults(items.map((item: any) => formatResult(entityType, item)))
				}
			} catch {
				if (!cancelled) {
					setResults([])
				}
			} finally {
				if (!cancelled) {
					setLoading(false)
				}
			}
		}
		fetchResults()
		return () => { cancelled = true }
	}, [entityType, payslipMonth, search])

	const handleSelect = async (result: SearchResult) => {
		const fkField = ENTITY_TYPE_TO_FK[entityType]
		if (taskId != null) {
			await assignManagerTaskLabelEntity(taskId, labelId, { [fkField]: result.id, dataName: result.displayName })
			onAssigned?.(result.displayName)
		} else {
			onSelect?.(fkField, result.id, result.displayName)
		}
	}

	return (
		<Drawer
			width={400}
			title={ENTITY_TYPE_LABELS[entityType]}
			visible
			onClose={onClose}
		>
			{entityType === LABEL_LINKED_ENTITY_TYPE.PAYSLIP && (
				<DatePicker
					picker="month"
					value={payslipMonth}
					onChange={(date) => {
						if (date) {
							setPayslipMonth(date)
						}
					}}
					allowClear={false}
					style={{
						width: '100%',
						marginBottom: 12
					}}
				/>
			)}
			<Input.Search
				placeholder={'Hľadať...'}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				style={{ marginBottom: 16 }}
			/>
			{loading ? (
				<div style={{
					display: 'flex',
					justifyContent: 'center',
					paddingTop: 32
				}}>
					<Spin />
				</div>
			) : (
				<List
					dataSource={results}
					renderItem={(item) => (
						<List.Item
							onClick={() => handleSelect(item)}
							style={{ cursor: 'pointer' }}
						>
							{item.displayName}
						</List.Item>
					)}
				/>
			)}
		</Drawer>
	)
}

export default EntitySearchDrawer
